import api from "@/lib/api";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
export const MAX_RETRIES = 3;
const PARALLEL = 3;

export class UploadPausedError extends Error {}

// Resumable chunked upload: init (server returns already-received chunks) → PUT missing chunks → complete.
export async function uploadFileChunked(file, { onProgress, onInit, signal }) {
  const init = (await api.post("/data-files/uploads/init", {
    filename: file.name, size: file.size, last_modified: file.lastModified,
  })).data;
  onInit?.(init);
  const { upload_id, chunk_size, total_chunks } = init;
  const sizeOf = (i) => (i < total_chunks - 1 ? chunk_size : file.size - chunk_size * i);
  const done = new Set(init.received);
  let confirmed = [...done].reduce((s, i) => s + sizeOf(i), 0);
  const inflight = {};
  const report = () => onProgress?.(confirmed + Object.values(inflight).reduce((s, v) => s + v, 0), file.size);
  report();
  const queue = [...Array(total_chunks).keys()].filter((i) => !done.has(i));

  const sendChunk = async (i) => {
    const blob = file.slice(i * chunk_size, i * chunk_size + sizeOf(i));
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        await api.put(`/data-files/uploads/${upload_id}/chunks/${i}`, blob, {
          headers: { "Content-Type": "application/octet-stream" }, signal, timeout: 180000,
          onUploadProgress: (e) => { inflight[i] = Math.min(e.loaded, sizeOf(i)); report(); },
        });
        delete inflight[i];
        confirmed += sizeOf(i);
        report();
        return;
      } catch (e) {
        delete inflight[i];
        if (signal?.aborted) throw e;
        if (attempt === MAX_RETRIES) throw new UploadPausedError(e?.response?.data?.detail || e.message);
        await sleep(1000 * 2 ** attempt);
      }
    }
  };

  const worker = async () => { while (queue.length) await sendChunk(queue.shift()); };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, queue.length || 1) }, worker));
  const fin = (await api.post(`/data-files/uploads/${upload_id}/complete`)).data;
  return { ...fin, resumed: init.resumed };
}

export async function cancelUpload(uploadId) {
  if (uploadId) await api.delete(`/data-files/uploads/${uploadId}`).catch(() => {});
}
